/**
 * Cliente HTTP del realm staff (T018; implementation-notes 002 §4, contracts/staff-api.md).
 *
 * Igual que el Admin (`apps/admin/src/api/client.ts`) **más** la firma del dispositivo:
 * - `baseUrl = import.meta.env.VITE_API_ORIGIN ?? ''` (vacío en local: proxy de Vite `/api`).
 * - Cookie `fermin_staff_session` → `credentials: 'include'`.
 * - Mutaciones llevan `X-Requested-With: fermin-staff` (anti-CSRF).
 * - Si el dispositivo ya tiene clave (`hasKey()`), toda petición lleva `X-Fermin-Device` firmada
 *   sobre el MISMO string de cuerpo que se envía (`JSON.stringify` una sola vez). En `enroll` y
 *   `claim` se pasa `includeDeviceId: false` para omitir `id=` (el servidor lo deriva de la clave).
 *   Sin clave (rutas públicas de activación) no se envía la cabecera.
 * - 2xx se valida con el esquema Zod de `@fermin/contracts`; `204`/cuerpo vacío → `undefined`.
 * - Errores → `ApiError` desde `ErrorEnvelopeSchema` y evento de `./events.ts` según el código
 *   (`EVENT_BY_ERROR_CODE`); `428 PRIVACY_NOTICE_REQUIRED` además enciende `privacy-store`.
 * - Nunca se loguea la cabecera de firma, tokens ni cuerpos.
 */
import {
  DEVICE_HEADER_NAME,
  ErrorEnvelopeSchema,
  STAFF_CSRF_HEADER_VALUE,
  type ErrorCode,
} from '@fermin/contracts';

import { hasKey } from '../device/keys';
import { signRequest } from '../device/sign';
import { setPrivacyRequired } from '../features/access/privacy-store';

import {
  emitFerminEvent,
  FERMIN_EVENTS,
  type FerminEventDetail,
  type FerminEventName,
} from './events';

import type { z } from 'zod';

/** Prefijo del realm staff. Los métodos reciben rutas relativas a él: `api.get('/me', …)`. */
export const STAFF_API_PREFIX = '/api/v1/staff';

/** Header anti-CSRF exigido por la API en toda mutación; valor `STAFF_CSRF_HEADER_VALUE`. */
export const CSRF_HEADER = 'X-Requested-With';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

const MUTATING_METHODS: readonly HttpMethod[] = ['POST', 'PATCH', 'DELETE'];

/** Código de error → evento que el root traduce en navegación (`routes/__root.tsx`). */
export const EVENT_BY_ERROR_CODE: Readonly<Partial<Record<ErrorCode, FerminEventName>>> =
  Object.freeze({
    UNAUTHENTICATED: FERMIN_EVENTS.unauthenticated,
    DEVICE_MISMATCH: FERMIN_EVENTS.deviceMismatch,
    DEVICE_CLOCK_SKEW: FERMIN_EVENTS.deviceMismatch,
    WORKER_INACTIVE: FERMIN_EVENTS.workerInactive,
    COMPANY_SUSPENDED: FERMIN_EVENTS.companySuspended,
    PRIVACY_NOTICE_REQUIRED: FERMIN_EVENTS.privacyRequired,
  });

/** Error normalizado del realm staff: el sobre de `contracts/errors.md` como excepción tipada. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string;

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
    requestId: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** `true` si la API respondió un código conocido del catálogo. */
  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

/** `fetch` no llegó al servidor (sin red, DNS, CORS). La UI muestra `errors.NETWORK`. */
export class NetworkError extends Error {
  readonly status = 0;

  constructor(cause?: unknown) {
    super('No pudimos conectarnos. Revisa tu conexión a internet.', { cause });
    this.name = 'NetworkError';
  }
}

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_ORIGIN ?? '';
}

/** Ruta absoluta sin origin: la que se firma y la que la API ve (`/api/v1/staff/me`). */
export function resolvePath(path: string): string {
  return path.startsWith('/api/') ? path : `${STAFF_API_PREFIX}${path}`;
}

function buildUrl(resolvedPath: string): string {
  return `${getApiBaseUrl()}${resolvedPath}`;
}

/** Construye el `ApiError` a partir de la respuesta; tolera cuerpos que no son el sobre esperado. */
async function toApiError(response: Response): Promise<ApiError> {
  const requestIdHeader = response.headers.get('X-Request-Id') ?? '';
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const parsed = ErrorEnvelopeSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, details, request_id } = parsed.data.error;
    return new ApiError({
      code,
      message,
      status: response.status,
      details: details as Record<string, unknown> | undefined,
      requestId: request_id || requestIdHeader,
    });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: 'Ocurrió un error inesperado. Inténtalo nuevamente.',
    status: response.status,
    details: undefined,
    requestId: requestIdHeader,
  });
}

/** Emite el evento asociado al código (si lo hay). El detalle nunca lleva tokens ni cabeceras. */
export function notifyApiError(error: ApiError): void {
  const eventName = EVENT_BY_ERROR_CODE[error.code];
  if (!eventName) {
    return;
  }
  if (eventName === FERMIN_EVENTS.privacyRequired) {
    setPrivacyRequired(true);
  }
  const detail: FerminEventDetail = {
    code: error.code,
    message: error.message,
    status: error.status,
    requestId: error.requestId,
  };
  emitFerminEvent(eventName, detail);
}

export interface RequestOptions<S extends z.ZodType = z.ZodType> {
  /** Se serializa con `JSON.stringify` UNA vez; el mismo string se envía y se firma. */
  body?: unknown;
  /** Esquema Zod de `@fermin/contracts` para validar la respuesta 2xx. */
  schema?: S;
  signal?: AbortSignal;
  /** `false` en `enroll` y `claim`: cabecera firmada pero sin `id=`. Por defecto `true`. */
  includeDeviceId?: boolean;
  /**
   * `false` para no emitir el evento global del código de error (006: el cierre de sesión del
   * Perfil ya decide su destino y un `423`/`401` no debe pasar antes por otra pantalla). El
   * `ApiError` se lanza igual. Por defecto `true`.
   */
  notifyErrors?: boolean;
}

async function request(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (MUTATING_METHODS.includes(method)) {
    headers[CSRF_HEADER] = STAFF_CSRF_HEADER_VALUE;
  }
  const bodyText = options.body === undefined ? undefined : JSON.stringify(options.body);
  if (bodyText !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const resolvedPath = resolvePath(path);
  if (await hasKey()) {
    headers[DEVICE_HEADER_NAME] = await signRequest({
      method,
      path: resolvedPath,
      body: bodyText,
      includeId: options.includeDeviceId !== false,
    });
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...(bodyText !== undefined ? { body: bodyText } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(buildUrl(resolvedPath), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (options.notifyErrors !== false) {
      notifyApiError(error);
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }

  const json: unknown = JSON.parse(text);
  return options.schema ? options.schema.parse(json) : undefined;
}

type WithSchema<S extends z.ZodType> = RequestOptions<S> & { schema: S };
type WithoutSchema = Omit<RequestOptions, 'schema'>;

function apiGet<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>> {
  return request('GET', path, options) as Promise<z.output<S>>;
}

function apiPost<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiPost(path: string, options?: WithoutSchema): Promise<undefined>;
function apiPost(path: string, options?: RequestOptions): Promise<unknown> {
  return request('POST', path, options);
}

function apiPatch<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiPatch(path: string, options?: WithoutSchema): Promise<undefined>;
function apiPatch(path: string, options?: RequestOptions): Promise<unknown> {
  return request('PATCH', path, options);
}

function apiDelete<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiDelete(path: string, options?: WithoutSchema): Promise<undefined>;
function apiDelete(path: string, options?: RequestOptions): Promise<unknown> {
  return request('DELETE', path, options);
}

/** Cliente único de Staff. Toda llamada a la API pasa por aquí. */
export const api = {
  get: apiGet,
  post: apiPost,
  patch: apiPatch,
  delete: apiDelete,
};
